import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import YatungDialog from '@Src/_basic/components/YatungDialog';
import YatungInput from '@Src/_basic/components/YatungInput';
import YatungSelect, { Options } from '@Src/_basic/components/YatungSelect';
import CodeIcon from '@Src/_basic/icons/Code';
import NameIcon from '@Src/_basic/icons/Name';
import TypeIcon from '@Src/_basic/icons/Type';

import YatungCheckBox from '@Src/_basic/components/YatungCheckBox';
import { PutMaterialNumberRequest } from '@Src/_basic/object/MaterialNumberManageType';
import { SpecificationData } from '@Src/_basic/object/SpecificationType';
import { TypeData } from '@Src/_basic/object/TypeType';
import EditForm from '../../../components/EditForm';
import { ItemType } from '../object/MaterialNumberType';

export interface SelectedItemType extends SpecificationData {
  type?: TypeData;
  sourceName?: string;
  typeNmae?: string;
  isYard?: boolean;
}

interface Props {
  open?: boolean;
  handleClose: () => void;
  handleSubmit: (data: PutMaterialNumberRequest) => void;
  allItems: Array<ItemType>;
  allTypes: Array<TypeData>;
  selectedItem: SelectedItemType;
  title: string;
  icon: JSX.Element;
}

const initData = {
  code: '',
  name: '',
  typeId: 0,
  id: 0,
};

export default function EditModal({
  open,
  handleClose,
  handleSubmit,
  allTypes,
  allItems,
  selectedItem,
  title,
  icon,
}: Props) {
  const { t: i18T } = useTranslation();
  const [data, setData] = useState<PutMaterialNumberRequest>(initData);
  const [typeOptions, setTypeOptions] = useState<Options[]>([]);
  const [isCodeDuplicated, setIsCodeDuplicated] = useState(false);
  const [toStorage, setToStorage] = useState<Array<number>>([]);

  const onSubmit = () => {
    if (data) {
      if (selectedItem.type) {
        handleSubmit({ ...data, isYard: toStorage[0] === 0 ? true : false });
      } else {
        handleSubmit(data);
      }
    }
  };

  useEffect(() => {
    const filter = allItems.filter((type) => type.code === data.code && type.code !== selectedItem?.code);
    setIsCodeDuplicated(Boolean(filter.length));
  }, [data.code]);

  useEffect(() => {
    if (selectedItem) {
      selectedItem.isYard === true ? setToStorage([0]) : setToStorage([1]);
      setData(selectedItem);
    }
  }, [selectedItem, handleClose]);

  useEffect(() => {
    if (allTypes.length) {
      setTypeOptions(allTypes.map(({ id, name }: any) => ({ value: id, text: name })));
    }
  }, [allTypes]);

  return (
    <YatungDialog
      open={open}
      handleClose={handleClose}
      icon={icon}
      title={title}
      body={
        <EditForm
          onSubmit={onSubmit}
          onCancel={handleClose}
          canSubmit={Boolean(data.code && data.name && data.typeId && !isCodeDuplicated)}
        >
          <Box
            sx={{ py: 2, display: 'flex', justifyContent: 'center', alignItems: 'stretch', flexDirection: 'column' }}
          >
            <Stack sx={{ borderBottom: '1px solid #6C708C', py: 1 }} direction="row" justifyContent="space-between">
              <Stack direction="row" sx={{ width: 120 }} alignItems="center">
                <CodeIcon />
                <Typography sx={{ pl: 1, fontWeight: 900, fontSize: 20 }}>
                  {i18T('APPLICATIONSMANAGEMENT.TABLE.CODE')}
                </Typography>
                <Typography sx={{ pl: 1, fontWeight: 900, fontSize: 12, color: '#FF000D' }}>{`*${i18T(
                  'APPLICATIONSMANAGEMENT.TABLE.CODE_NUM',
                  { num: 2 },
                )}`}</Typography>
              </Stack>
              <Stack direction="row" sx={{ width: 240 }} alignItems="center">
                <NameIcon />
                <Typography sx={{ pl: 1, fontWeight: 900, fontSize: 20 }}>
                  {i18T('APPLICATIONSMANAGEMENT.TABLE.NAME')}
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ width: 120 }} alignItems="center">
                <TypeIcon />
                <Typography sx={{ pl: 1, fontWeight: 900, fontSize: 20 }}>
                  {i18T('APPLICATIONSMANAGEMENT.TABLE.TYPE')}
                </Typography>
              </Stack>
            </Stack>
            <Stack sx={{ pt: 4 }} direction="row" justifyContent="space-between" alignItems="center">
              <YatungInput
                inputProps={{
                  maxLength: 2,
                }}
                onChange={(e: any) => setData({ ...data, code: e.target.value.replace(/[^a-zA-Z0-9]/g, '') })}
                value={data['code']}
                error={isCodeDuplicated}
                helperText={isCodeDuplicated && i18T('APPLICATIONSMANAGEMENT.TABLE.CODE_REPEAT')}
                sx={{ m: 1, width: 120 }}
              />
              <YatungInput onChange={(e: any) => setData({ ...data, name: e.target.value })} value={data['name']} />
              <YatungSelect
                width="120px"
                disabled={typeOptions.length === 0}
                options={typeOptions}
                value={data.typeId}
                onChange={(e) => setData({ ...data, typeId: e.target.value as number })}
              />
            </Stack>
            {selectedItem.type && (
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography sx={{ pl: 1, fontWeight: 900, fontSize: 20 }}>{`${i18T(
                  'APPLICATIONSMANAGEMENT.MATERIAL_NUMBER_MANAGE.STORAGE',
                )} :`}</Typography>
                <YatungCheckBox
                  options={[
                    {
                      value: 0,
                      text: i18T('GLOBAL.YES'),
                    },
                    {
                      value: 1,
                      text: i18T('GLOBAL.NOT'),
                    },
                  ]}
                  onChange={(e) => setToStorage([parseInt(e.target.value)])}
                  labelTextStyle={{ fontSize: 20 }}
                  initialCheckedValue={toStorage}
                />
              </Stack>
            )}
          </Box>
        </EditForm>
      }
    />
  );
}
